import React, { useRef } from "react";
import styled from "styled-components";
import { FiRotateCcw, FiSearch, FiX } from "react-icons/fi";
import { Field } from "../shared";
import { useLanguage } from "../../../contexts/LanguageContext";

const SearchWrap = styled.div`
  position: relative;
  min-width: 0;
  > svg.lead {
    position: absolute;
    left: 14px;
    top: 50%;
    width: 16px;
    height: 16px;
    transform: translateY(-50%);
    color: ${(p) => p.theme.colors.textMuted};
    pointer-events: none;
  }
  input {
    min-height: 44px;
    padding: 9px 40px 9px 40px;
    font-size: 15px;
    &::-webkit-search-cancel-button,
    &::-webkit-search-decoration {
      -webkit-appearance: none;
      appearance: none;
    }
  }
  button.clear {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    color: ${(p) => p.theme.colors.textMuted};
    &:hover {
      background: ${(p) => p.theme.colors.surfaceHover};
      color: ${(p) => p.theme.colors.text};
    }
  }
`;

/** Строка поиска: крестик очищает только текст, не трогая остальные фильтры. */
export function SearchBox({
  value,
  onChange,
  label,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  autoFocus?: boolean;
}) {
  const { t } = useLanguage();
  const input = useRef<HTMLInputElement>(null);
  return (
    <SearchWrap>
      <FiSearch className="lead" aria-hidden="true" />
      <Field
        ref={input}
        autoFocus={autoFocus}
        type="search"
        aria-label={label}
        placeholder={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button
          type="button"
          className="clear"
          aria-label={t("skins.clear_search")}
          title={t("skins.clear_search")}
          onClick={() => {
            onChange("");
            input.current?.focus();
          }}
        >
          <FiX aria-hidden="true" />
        </button>
      )}
    </SearchWrap>
  );
}

const ChipBox = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  min-height: 30px;
  padding: 0 4px 0 11px;
  border-radius: 8px;
  background: ${(p) => p.theme.colors.primarySoft};
  border: 1px solid #ff9b5740;
  color: ${(p) => p.theme.colors.text};
  font-size: 13px;
  font-weight: 600;
  .txt {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  button {
    flex: none;
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    color: ${(p) => p.theme.colors.textSecondary};
    &:hover {
      background: #ff9b5726;
      color: ${(p) => p.theme.colors.text};
    }
  }
`;

/** Метка активного фильтра; крестик снимает только его. */
export function FilterChip({
  children,
  removeLabel,
  onRemove,
}: {
  children: React.ReactNode;
  removeLabel: string;
  onRemove: () => void;
}) {
  return (
    <ChipBox>
      <span className="txt">{children}</span>
      <button type="button" aria-label={removeLabel} title={removeLabel} onClick={onRemove}>
        <FiX aria-hidden="true" />
      </button>
    </ChipBox>
  );
}

const ResetBtn = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 30px;
  padding: 0 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textSecondary};
  transition:
    color ${(p) => p.theme.motion.fast},
    background ${(p) => p.theme.motion.fast};
  svg {
    width: 14px;
    height: 14px;
  }
  &:hover {
    background: ${(p) => p.theme.colors.surfaceHover};
    color: ${(p) => p.theme.colors.text};
  }
`;

/** Спокойное текстовое действие «Сбросить всё» с небольшим значком. */
export function ResetButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <ResetBtn type="button" onClick={onClick}>
      <FiRotateCcw aria-hidden="true" />
      {label}
    </ResetBtn>
  );
}

/** Подпись состояния рядом с моделью: «Прямо с завода» и т. п. */
export const wearLabelKey = (condition?: string) =>
  condition ? `skins.wear.${condition}` : "";
